import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { supabase } from '../../../lib/supabase';
import RichTextEditor from './components/RichTextEditor';

type Category = '維修案例' | '優惠活動' | '公司動態' | '維修教學';
const CATEGORIES: Category[] = ['維修案例', '優惠活動', '公司動態', '維修教學'];

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: Category;
  image: string;
  published_at: string;
  published: boolean;
}

const EMPTY: Omit<NewsArticle, 'id'> = { title: '', content: '', summary: '', category: '維修案例', image: '', published_at: new Date().toISOString().split('T')[0], published: false };
const catColors: Record<string, string> = { '維修案例': 'bg-blue-50 text-blue-600', '優惠活動': 'bg-orange-50 text-orange-600', '公司動態': 'bg-green-50 text-green-600', '維修教學': 'bg-purple-50 text-purple-600' };

export default function AdminNewsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<NewsArticle[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Omit<NewsArticle, 'id'>>(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [imgPreview, setImgPreview] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem('admin_auth')) navigate('/admin');
    fetchNews();
  }, [navigate]);

  const fetchNews = async () => {
    try {
      const { data: news, error } = await supabase
        .from('news_articles')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) throw error;
      setData(news || []);
    } catch (err) {
      console.error('Failed to fetch news:', err);
      showToast('載入失敗');
    }
  };

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const handleSave = async () => {
    if (!form.title || !form.content) { showToast('標題與內容為必填'); return; }
    setLoading(true);
    try {
      // Strip HTML tags for auto summary
      const plainText = form.content.replace(/<[^>]*>/g, '').trim();
      const autoSummary = form.summary || plainText.substring(0, 100) + '...';
      const payload = { ...form, summary: autoSummary };

      if (editId) {
        const { error } = await supabase
          .from('news_articles')
          .update(payload)
          .eq('id', editId);
        if (error) throw error;
        showToast('已更新');
      } else {
        const { error } = await supabase
          .from('news_articles')
          .insert([{ ...payload, id: crypto.randomUUID() }]);
        if (error) throw error;
        showToast('已新增');
      }
      await fetchNews();
      setForm(EMPTY); setEditId(null); setShowForm(false); setImgPreview('');
    } catch (err) {
      console.error('Failed to save:', err);
      showToast('儲存失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: NewsArticle) => {
    const { id, ...rest } = item;
    setEditId(id); setForm(rest); setShowForm(true); setImgPreview(rest.image ?? '');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此文章？')) return;
    try {
      const { error } = await supabase.from('news_articles').delete().eq('id', id);
      if (error) throw error;
      await fetchNews();
      showToast('已刪除');
    } catch (err) {
      console.error('Failed to delete:', err);
      showToast('刪除失敗');
    }
  };

  const togglePublish = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('news_articles')
        .update({ published: !currentStatus })
        .eq('id', id);
      if (error) throw error;
      await fetchNews();
      showToast('狀態已更新');
    } catch (err) {
      console.error('Failed to toggle publish:', err);
      showToast('更新失敗');
    }
  };

  const handleImgChange = (url: string) => {
    setForm({ ...form, image: url });
    setImgPreview(url);
  };

  const handleImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) { showToast('僅支援 JPG、PNG、WebP、GIF 格式'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('圖片大小不能超過 5MB'); return; }
    setLoading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('news-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('news-images').getPublicUrl(fileName);
      handleImgChange(urlData.publicUrl);
      showToast('圖片上傳成功');
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('圖片上傳失敗，請重試');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <AdminLayout>
      {toast && <div className="fixed top-6 right-6 z-50 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl">{toast}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">最新消息管理</h1>
          <p className="text-gray-500 text-sm mt-1">共 {data.length} 篇，已發布 {data.filter(n => n.published).length} 篇</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(!showForm); setImgPreview(''); }} className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap">
          {showForm ? '收起' : '+ 新增文章'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-orange-100 p-6 mb-6">
          <h2 className="font-bold text-gray-900 mb-5">{editId ? '編輯文章' : '新增文章'}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="lg:col-span-2">
              <label className="text-xs text-gray-600 mb-1 block">標題 *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="文章標題" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">分類</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value as Category })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">發布日期</label>
              <input type="date" value={form.published_at} onChange={e => setForm({ ...form, published_at: e.target.value })} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>

            {/* Image Upload + URL */}
            <div className="lg:col-span-2">
              <label className="text-xs text-gray-600 mb-1 block">封面圖片</label>
              <div className="flex gap-3 items-start">
                <div className="flex-1 space-y-2">
                  {/* Upload button */}
                  <label className={`flex items-center gap-2 w-full border-2 border-dashed border-gray-200 hover:border-orange-400 rounded-xl px-4 py-3 cursor-pointer transition-colors ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <i className="ri-upload-cloud-2-line text-orange-400 text-lg" />
                    <span className="text-sm text-gray-500">{loading ? '上傳中...' : '點擊上傳圖片（JPG / PNG / WebP，最大 5MB）'}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleImgUpload} />
                  </label>
                  {/* URL input */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 whitespace-nowrap">或貼上網址</span>
                    <input
                      value={form.image}
                      onChange={e => handleImgChange(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-orange-400"
                    />
                  </div>
                </div>
                {imgPreview && (
                  <div className="w-28 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 relative">
                    <img src={imgPreview} alt="預覽" className="w-full h-full object-cover" onError={() => setImgPreview('')} />
                    <button
                      type="button"
                      onClick={() => { setImgPreview(''); setForm(f => ({ ...f, image: '' })); }}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <label className="text-xs text-gray-600 mb-1 block">摘要（選填，留空自動截取內文）</label>
              <input value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} placeholder="文章摘要..." className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div className="lg:col-span-2">
              <label className="text-xs text-gray-600 mb-1 block">內文 *</label>
              <RichTextEditor
                value={form.content}
                onChange={html => setForm(f => ({ ...f, content: html }))}
                placeholder="請輸入文章內容，支援標題、粗體、清單、插入圖片..."
              />
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-orange-500" />
                <span className="text-sm text-gray-700">立即發布</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={loading} className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50">{loading ? '儲存中...' : (editId ? '儲存變更' : '新增文章')}</button>
            <button onClick={() => { setShowForm(false); setEditId(null); setImgPreview(''); }} className="px-6 py-2.5 bg-gray-100 text-gray-700 text-sm rounded-xl cursor-pointer whitespace-nowrap">取消</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {data.map(article => (
          <div key={article.id} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
            {article.image ? (
              <img src={article.image} alt="" className="w-20 h-14 object-cover rounded-xl flex-shrink-0" />
            ) : (
              <div className="w-20 h-14 bg-gray-100 rounded-xl flex-shrink-0 flex items-center justify-center">
                <i className="ri-image-line text-gray-300 text-xl" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catColors[article.category] ?? 'bg-gray-100 text-gray-600'}`}>{article.category}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${article.published ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{article.published ? '已發布' : '草稿'}</span>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm line-clamp-1">{article.title}</h3>
              <p className="text-gray-400 text-xs mt-0.5">{article.published_at}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => togglePublish(article.id, article.published)} className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer whitespace-nowrap ${article.published ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                {article.published ? '取消發布' : '發布'}
              </button>
              <button onClick={() => handleEdit(article)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-lg cursor-pointer whitespace-nowrap">編輯</button>
              <button onClick={() => handleDelete(article.id)} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 text-xs rounded-lg cursor-pointer whitespace-nowrap">刪除</button>
            </div>
          </div>
        ))}
        {data.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">尚無文章</div>
        )}
      </div>
    </AdminLayout>
  );
}